import React from 'react';
import {
    Package,
    AlertTriangle,
    Search,
    Inbox,
    FileText,
    Database,
    Users,
    Settings,
    Zap,
    Activity,
    Shield
} from 'lucide-react';

const icons = {
    package: Package,
    alert: AlertTriangle,
    search: Search,
    inbox: Inbox,
    file: FileText,
    database: Database,
    users: Users,
    settings: Settings,
    zap: Zap,
    activity: Activity,
    shield: Shield
};

export const EmptyState = ({
    icon = 'inbox',
    title,
    description,
    actionLabel,
    onAction,
    actionIcon,
    secondaryActionLabel,
    onSecondaryAction,
    className = ''
}) => {
    const Icon = icons[icon] || icons.inbox;
    const ActionIcon = actionIcon;

    return (
        <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}>
            {/* Icon with gradient background */}
            <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full blur-2xl opacity-60" />
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center border-2 border-white shadow-lg">
                    <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-blue-500" />
                </div>
            </div>

            {/* Title */}
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                {title}
            </h3>

            {/* Description */}
            {description && (
                <p className="text-gray-500 max-w-md mb-8 text-sm sm:text-base">
                    {description}
                </p>
            )}

            {/* Actions */}
            {(actionLabel || secondaryActionLabel) && (
                <div className="flex flex-col sm:flex-row gap-3">
                    {actionLabel && onAction && (
                        <button
                            onClick={onAction}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            {ActionIcon && <ActionIcon className="w-4 h-4" />}
                            {actionLabel}
                        </button>
                    )}
                    {secondaryActionLabel && onSecondaryAction && (
                        <button
                            onClick={onSecondaryAction}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 rounded-lg font-medium border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                        >
                            {secondaryActionLabel}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Specific empty states for common scenarios
export const NoDevicesFound = ({ onAddDevice, onClearFilters, hasFilters }) => (
    <EmptyState
        icon="search"
        title={hasFilters ? "No devices match your filters" : "No devices yet"}
        description={
            hasFilters
                ? "Try adjusting your search or filter criteria to find what you're looking for."
                : "Get started by adding your first IoT device to the platform. You can configure sensors, set up alerts, and monitor device health."
        }
        actionLabel={hasFilters ? "Clear Filters" : "Add Device"}
        onAction={hasFilters ? onClearFilters : onAddDevice}
        actionIcon={hasFilters ? null : Package}
        secondaryActionLabel={hasFilters ? "Add Device" : null}
        onSecondaryAction={hasFilters ? onAddDevice : null}
    />
);

export const NoAlertsFound = ({ onClearFilters, hasFilters }) => (
    <EmptyState
        icon={hasFilters ? "search" : "shield"}
        title={hasFilters ? "No alerts match your filters" : "All clear!"}
        description={
            hasFilters
                ? "Try adjusting your filters to see more alerts."
                : "No active alerts at the moment. Your devices are running smoothly."
        }
        actionLabel={hasFilters ? "Clear Filters" : null}
        onAction={hasFilters ? onClearFilters : null}
    />
);

export const NoDataAvailable = ({ title, description, onRefresh }) => (
    <EmptyState
        icon="database"
        title={title || "No data available"}
        description={description || "There's no data to display at the moment. Try refreshing or check back later."}
        actionLabel="Refresh"
        onAction={onRefresh}
        actionIcon={Activity}
    />
);

export const ErrorState = ({ title, description, onRetry }) => (
    <EmptyState
        icon="alert"
        title={title || "Something went wrong"}
        description={description || "We encountered an error while loading this data. Please try again."}
        actionLabel="Try Again"
        onAction={onRetry}
    />
);

export default EmptyState;
